import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { getRouterSelectors, routerNavigatedAction } from '@ngrx/router-store';
import {
  createActionGroup,
  emptyProps,
  props,
  createFeature,
  createReducer,
  on,
  createSelector,
  Store,
} from '@ngrx/store';
import {
  withLatestFrom,
  switchMap,
  map,
  catchError,
  of,
  tap,
  distinctUntilChanged,
  filter,
  Observable,
} from 'rxjs';
import {
  ColumnFilter,
  DataRequest,
  GridSort,
} from 'src/app/shared/table/components/table/table.types';
import {
  createInitialPageDataState,
  pageDataStateToParams,
  DataFeatureConfig,
  DEFAULT_GRID_DATA_QUERY_PARAMS_CONFIG,
  FetchingFn,
  DEFAULT_PAGE_SIZE,
  filterEq,
  filterToQueryParam,
  RequestDataFn,
  gridSortToQueryParams,
} from './grid-data.types';
import { StringLiteralCheck } from 'src/app/shared/util/string-literal-check';
import {
  GridDataSourceMode,
  GridDataSourceState,
} from './grid-data-source-state';

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @param config Required configuration: router and filtering.
 * @returns NGRX feature, actions, and effect builder.
 *
 * The NGRX feature implements the following data flow for grids.
 * 1. Whenever pagination, filter, or sorting changes, relevant query params are
 * updated.
 * 2. Whenever relevant query params are updated, data is fetched.
 *
 * The data is fetched asynchronously from effects.  It is assumed that data
 * comes in through an observable of the type GridDataSourceState<TData>, which
 * combines data with loading status.  Whenever the effect requires data, it
 * calls the thunk requestData (or refreshData) provided in the configuration.
 * It is assumed that this call will result in updates of the incoming
 * observable.  However, the feature itself does not take care of this.
 *
 * The component receives the data including loading status via the
 * `selectGridData` selector.
 *
 * The following actions are supported:
 *   refresh()
 *   dataRequest(request)
 *   dataLoadSuccess(result)
 *   dataLoadFailure({ error? })
 *   applyFilter({ filter })
 *   applySorting({ sort })
 *
 * The `dataRequest` action is dispatched when pagination, filtering, or sorting
 * changes.
 *
 * The `refresh` action can be called to force the reload of the data.
 *
 * The `dataLoadSuccess` and `dataLoadFailure` are dispatched internally by the
 * fetching mechanism.  They can be used e. g. in toast creating effects.
 *
 * The `applyFilter` and `applySorting` actions are dispatched by the client.
 * They result in navigation.
 *
 * The effect builder is a function returning effects that should be embedded in
 * page's effects.  Internally, the effects do the following:
 * - dataRequest$: updates query params
 * - dataUpdated$: updates grid data when new data is available
 * - refresh$: reloads the data according to current query params
 * - navigated$: listens to URL changes and refreshes the data as needed
 */
export function createGridDataFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  TData,
>(featureName: TActionSource, config: DataFeatureConfig) {
  const queryParamsConfig =
    config.queryParamsConfig ?? DEFAULT_GRID_DATA_QUERY_PARAMS_CONFIG;
  const actions = createActionGroup({
    source: featureName,
    events: {
      Refresh: emptyProps(),
      'Data Request': props<DataRequest>(),
      'Data Loading': emptyProps(),
      'Data Load Success': props<{
        rowData: TData[];
        totalRecords: number;
      }>(),
      'Data Load Failure': props<{ error?: string }>(),
      'Apply Filter': props<{ filter: ColumnFilter }>(),
      'Apply Sorting': props<{ sort: GridSort }>(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer(
      createInitialPageDataState<TData>(),
      on(actions.dataLoading, state => ({ ...state, loading: true })),
      on(actions.dataLoadSuccess, (state, { rowData, totalRecords }) => ({
        ...state,
        loading: false,
        rowData,
        totalRecords,
      })),
      on(actions.dataLoadFailure, state => ({
        ...state,
        loading: false,
        rowData: [],
        totalRecords: 0,
      })),
    ),
    extraSelectors: baseSelectors => {
      const { selectQueryParams } = getRouterSelectors();
      const selectGridData = createSelector(
        baseSelectors.selectLoading,
        baseSelectors.selectRowData,
        baseSelectors.selectTotalRecords,
        selectQueryParams,
        (loading, rowData, totalRecords, queryParams) =>
          pageDataStateToParams<TData>(
            {
              loading,
              rowData,
              totalRecords,
            },
            queryParams,
            queryParamsConfig,
          ),
      );
      const selectGridDataParams = createSelector(selectGridData, gridData => ({
        page: gridData.paginationState.currentPage,
        pageSize: gridData.paginationState.recordsPerPage,
        filter: gridData.filter,
        sort: gridData.sort,
      }));
      return { selectGridData, selectGridDataParams };
    },
  });
  const createEffects = (
    action$: Actions,
    store: Store,
    dataSourceState$: Observable<GridDataSourceState<TData>>,
    router: Router,
    options: {
      requestData: RequestDataFn;
      refreshData: RequestDataFn;
    },
  ) => ({
    dataRequest$: createEffect(
      () =>
        action$.pipe(
          ofType(actions.dataRequest),
          tap(action => {
            const page = action.page === 1 ? null : action.page;
            const pageSize =
              action.pageSize === DEFAULT_PAGE_SIZE ? null : action.pageSize;
            const queryParams = {
              [queryParamsConfig.page]: page,
              [queryParamsConfig.pageSize]: pageSize,
            };
            router.navigate([], { queryParams, queryParamsHandling: 'merge' });
          }),
        ),
      { dispatch: false },
    ),
    applyFilter$: createEffect(() =>
      action$.pipe(
        ofType(actions.applyFilter),
        distinctUntilChanged((previous, current) =>
          filterEq(previous.filter, current.filter),
        ),
        tap(action => {
          const queryParams = {
            [queryParamsConfig.page]: null, // new filter: go to page 1
            [queryParamsConfig.q]:
              action.filter.length > 0
                ? filterToQueryParam(action.filter)
                : null,
          };
          router.navigate([], { queryParams, queryParamsHandling: 'merge' });
        }),
      ),
    ),
    applySorting$: createEffect(() =>
      action$.pipe(
        ofType(actions.applySorting),
        distinctUntilChanged((previous, current) =>
          GridSort.eq(previous.sort, current.sort),
        ),
        tap(action => {
          const sortingQueryParams = gridSortToQueryParams(
            action.sort,
            queryParamsConfig,
          );
          const queryParams = {
            [queryParamsConfig.page]: null, // new sorting: go to page 1
            ...sortingQueryParams,
          };
          router.navigate([], { queryParams, queryParamsHandling: 'merge' });
        }),
      ),
    ),
    // Refresh data after navigation if needed
    navigated$: createEffect(
      () =>
        action$.pipe(
          ofType(routerNavigatedAction),
          filter(action => config.routeFilter(action.payload.routerState)),
          withLatestFrom(store.select(feature.selectGridData)),
          map(([, gridDataParams]) => gridDataParams),
          distinctUntilChanged(
            (previous, current) =>
              previous.paginationState.currentPage ===
                current.paginationState.currentPage &&
              previous.paginationState.recordsPerPage ===
                current.paginationState.recordsPerPage &&
              filterEq(previous.filter, current.filter) &&
              GridSort.eq(previous.sort, current.sort),
          ),
          tap(gridDataParams => {
            const { filter, sort } = gridDataParams;
            const { currentPage, recordsPerPage } =
              gridDataParams.paginationState;
            const offset = (currentPage - 1) * recordsPerPage;
            const limit = recordsPerPage;
            options.requestData(offset, limit, filter, sort);
          }),
        ),
      { dispatch: false },
    ),
    dataUpdated$: createEffect(() =>
      dataSourceState$.pipe(
        switchMap(dataSourceState => {
          if (dataSourceState.mode === GridDataSourceMode.standby) {
            return of(
              actions.dataLoadSuccess({
                rowData: [],
                totalRecords: 0,
              }),
            );
          }
          if (dataSourceState.mode === GridDataSourceMode.loading) {
            return of(actions.dataLoading());
          }
          if (dataSourceState.mode === GridDataSourceMode.loaded) {
            return of(
              actions.dataLoadSuccess({
                rowData: dataSourceState.data.rowData,
                totalRecords: dataSourceState.data.rowCount,
              }),
            );
          }
          if (dataSourceState.mode === GridDataSourceMode.error) {
            return of(actions.dataLoadFailure({}));
          }
          const exhaustivenessCheck = dataSourceState;
          return exhaustivenessCheck;
        }),
      ),
    ),
    refresh$: createEffect(
      () =>
        action$.pipe(
          ofType(actions.refresh),
          withLatestFrom(store.select(feature.selectGridData)),
          tap(([, gridData]) => {
            const { filter, sort } = gridData;
            const { currentPage, recordsPerPage } = gridData.paginationState;
            const offset = (currentPage - 1) * recordsPerPage;
            const limit = recordsPerPage;
            options.refreshData(offset, limit, filter, sort);
          }),
        ),
      { dispatch: false },
    ),
  });
  return { feature, actions, createEffects };
}

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @param config Required configuration: router and filtering.
 * @returns NGRX feature, actions, and effect builder.
 *
 * The NGRX feature implements the following data flow for grids.
 * 1. Whenever pagination, filter, or sorting changes, relevant query params are
 * updated.
 * 2. Whenever relevant query params are updated, data is fetched.
 *
 * The component receives the data including loading status via the
 * `selectGridData` selector.
 *
 * The following actions are supported:
 *   refresh()
 *   dataRequest(request)
 *   dataLoadSuccess(result)
 *   dataLoadFailure({ error? })
 *   applyFilter({ filter })
 *   applySorting({ sort })
 *
 * The `dataRequest` action is dispatched when pagination, filtering, or sorting
 * changes.
 *
 * The `refresh` action can be called to force the reload of the data.
 *
 * The `dataLoadSuccess` and `dataLoadFailure` are dispatched internally by the
 * fetching mechanism.  They can be used e. g. in toast creating effects.
 *
 * The `applyFilter` and `applySorting` actions are dispatched by the client.
 * They result in navigation.
 *
 * The effect builder is a function returning effects that should be embedded in
 * page's effects.  Internally, the effects do the following:
 * - dataRequest$: updates query params
 * - refresh$: reloads the data according to current query params
 * - navigated$: listens to URL changes and refreshes the data as needed
 */
export function createGridDataFeatureWithFetchingFn<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  TData,
>(featureName: TActionSource, config: DataFeatureConfig) {
  const queryParamsConfig =
    config.queryParamsConfig ?? DEFAULT_GRID_DATA_QUERY_PARAMS_CONFIG;
  const actions = createActionGroup({
    source: featureName,
    events: {
      Refresh: emptyProps(),
      'Data Request': props<DataRequest>(),
      'Data Load Success': props<{
        rowData: TData[];
        totalRecords: number;
      }>(),
      'Data Load Failure': props<{ error?: string }>(),
      'Apply Filter': props<{ filter: ColumnFilter }>(),
      'Apply Sorting': props<{ sort: GridSort }>(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer(
      createInitialPageDataState<TData>(),
      on(actions.refresh, state => ({ ...state, loading: true })),
      on(actions.dataLoadSuccess, (state, { rowData, totalRecords }) => ({
        ...state,
        loading: false,
        rowData,
        totalRecords,
      })),
      on(actions.dataLoadFailure, state => ({
        ...state,
        loading: false,
        rowData: [],
        totalRecords: 0,
      })),
    ),
    extraSelectors: baseSelectors => {
      const { selectQueryParams } = getRouterSelectors();
      const selectPageDataParams = createSelector(
        baseSelectors.selectLoading,
        baseSelectors.selectRowData,
        baseSelectors.selectTotalRecords,
        selectQueryParams,
        (loading, rowData, totalRecords, queryParams) =>
          pageDataStateToParams<TData>(
            {
              loading,
              rowData,
              totalRecords,
            },
            queryParams,
            queryParamsConfig,
          ),
      );
      return { selectPageDataParams };
    },
  });
  const createEffects = (
    action$: Actions,
    fetchData: FetchingFn<TData>,
    store: Store,
    router: Router,
  ) => ({
    refresh$: createEffect(() =>
      action$.pipe(
        ofType(actions.refresh),
        withLatestFrom(store.select(feature.selectPageDataParams)),
        switchMap(([, dataParams]) => {
          const { paginationState, filter, sort: sorting } = dataParams;
          const { currentPage, recordsPerPage } = paginationState;
          const offset = (currentPage - 1) * recordsPerPage;
          const limit = recordsPerPage;
          return fetchData(offset, limit, filter, sorting).pipe(
            map(({ rowData, rowCount }) => {
              return actions.dataLoadSuccess({
                rowData: rowData,
                totalRecords: rowCount,
              });
            }),
            catchError(() => of(actions.dataLoadFailure({}))),
          );
        }),
      ),
    ),
    dataRequest$: createEffect(
      () =>
        action$.pipe(
          ofType(actions.dataRequest),
          tap(action => {
            const page = action.page === 1 ? null : action.page;
            const pageSize =
              action.pageSize === DEFAULT_PAGE_SIZE ? null : action.pageSize;
            const queryParams = {
              [queryParamsConfig.page]: page,
              [queryParamsConfig.pageSize]: pageSize,
            };
            router.navigate([], { queryParams, queryParamsHandling: 'merge' });
          }),
        ),
      { dispatch: false },
    ),
    applyFilter$: createEffect(() =>
      action$.pipe(
        ofType(actions.applyFilter),
        distinctUntilChanged((previous, current) =>
          filterEq(previous.filter, current.filter),
        ),
        tap(action => {
          const queryParams = {
            [queryParamsConfig.page]: null, // new filter: go to page 1
            [queryParamsConfig.q]:
              action.filter.length > 0
                ? filterToQueryParam(action.filter)
                : null,
          };
          router.navigate([], { queryParams, queryParamsHandling: 'merge' });
        }),
      ),
    ),
    applySorting$: createEffect(() =>
      action$.pipe(
        ofType(actions.applySorting),
        distinctUntilChanged((previous, current) =>
          GridSort.eq(previous.sort, current.sort),
        ),
        tap(action => {
          const sortingQueryParams = gridSortToQueryParams(
            action.sort,
            queryParamsConfig,
          );
          const queryParams = {
            [queryParamsConfig.page]: null, // new sorting: go to page 1
            ...sortingQueryParams,
          };
          router.navigate([], { queryParams, queryParamsHandling: 'merge' });
        }),
      ),
    ),
    // Refresh data after navigation if needed
    navigated$: createEffect(() =>
      action$.pipe(
        ofType(routerNavigatedAction),
        filter(action => config.routeFilter(action.payload.routerState)),
        withLatestFrom(store.select(feature.selectPageDataParams)),
        map(([, dataParams]) => dataParams),
        distinctUntilChanged(
          (previous, current) =>
            previous.paginationState.currentPage ===
              current.paginationState.currentPage &&
            previous.paginationState.recordsPerPage ===
              current.paginationState.recordsPerPage &&
            filterEq(previous.filter, current.filter) &&
            GridSort.eq(previous.sort, current.sort),
        ),
        map(() => actions.refresh()),
      ),
    ),
  });
  return { feature, actions, createEffects };
}
